import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { ExpressionField } from '../../../common';
import { FieldFormulaBase } from '../field-formula.base';
import {
  AGGREGATE_FUNCTIONS,
  AggregateToken,
  DATE_PARTS,
  DateToken,
  FormulaOperator,
  FormulaToken,
  NumberToken,
  OPERATORS,
  OperatorToken,
  dateFunctionFor,
  newDateToken,
} from '../formula';
import { insertableAt, tokenText } from '../formula-typing';
import { FormulaStatusComponent } from '../formula-status/formula-status.component';

export interface FormulaFieldGroup {
  label: string;
  fields: { field: ExpressionField; label: string }[];
}

const DIGIT = /^[\d.]$/;
const LETTER = /^\p{L}$/u;
const MODULE_PREFIX = /^\[(.*?)\]\s*(.*)$/;
const SYMBOLS: { value: string; label: string }[] = [
  ...OPERATORS.map(({ value, label }) => ({ value, label })),
  { value: '(', label: '(' },
  { value: ')', label: ')' },
];

@Component({
  selector: 'cipo-field-formula-editor',
  imports: [
    FlexLayoutModule,
    FormulaStatusComponent,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatSelectModule,
    NgTemplateOutlet,
    NgxSkeletonLoaderModule,
    TranslateModule,
  ],
  templateUrl: './formula-editor.component.html',
  styleUrl: './formula-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: FieldFormulaBase, useExisting: forwardRef(() => FieldFormulaEditorComponent) }],
})
export class FieldFormulaEditorComponent extends FieldFormulaBase {
  private readonly translate = inject(TranslateService);
  private readonly area = viewChild<ElementRef<HTMLElement>>('area');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchBox');

  private readonly caretIndex = signal(Number.MAX_SAFE_INTEGER);
  /** Where the next token goes: 0 is before the first token, `tokens().length` after the last, where it starts. */
  readonly caret = computed(() => Math.min(this.caretIndex(), this.tokens().length));
  readonly selected = signal<number>(null);
  /** Digits typed at the caret, not a token yet. */
  readonly pending = signal('');
  readonly search = signal('');
  readonly focused = signal(false);

  readonly functions = AGGREGATE_FUNCTIONS;
  readonly dateParts = DATE_PARTS;
  readonly symbols = SYMBOLS;

  readonly selectedToken = computed(() => this.tokens()[this.selected() ?? -1]);
  readonly selectedFunction = computed(() => this.tokenOf<AggregateToken>('function'));
  readonly selectedDate = computed(() => this.tokenOf<DateToken>('date'));
  readonly selectedOperator = computed(() => this.tokenOf<OperatorToken>('operator'));
  readonly selectedNumber = computed(() => this.tokenOf<NumberToken>('number'));

  readonly dateFunction = computed(() => dateFunctionFor(this.isDateResult()));

  /** The typed digits as a number: `5.` is 5 and `.5` is 0.5, while a lone dot is not one yet. */
  private readonly pendingValue = computed(() => {
    const text = this.pending();
    return text && text !== '.' ? Number(text) : null;
  });

  /** What may go in at the caret, with the digits being typed already counted as a number before it. */
  readonly allowed = computed(() => {
    const tokens = this.tokens();
    const at = this.caret();
    const value = this.pendingValue();
    if (value === null) {
      return insertableAt(tokens, at, this.isDateResult());
    }
    const number: FormulaToken = { type: 'number', value };
    return insertableAt([...tokens.slice(0, at), number, ...tokens.slice(at)], at + 1, this.isDateResult());
  });

  readonly insertableSymbols = computed(() => {
    const { operators, open, close } = this.allowed();
    return new Set<string>([...operators, ...(open ? ['('] : []), ...(close ? [')'] : [])]);
  });

  /** A field or a function from the list goes in at the caret, or onto the selected function. */
  readonly acceptsValue = computed(() => !!this.selectedFunction() || this.allowed().value);

  readonly fieldGroups = computed<FormulaFieldGroup[]>(() => {
    if (this.isDateResult()) {
      return [];
    }
    const query = this.search().trim().toLowerCase();
    const groups = new Map<string, FormulaFieldGroup>();
    this.numericFields()
      .filter(field => !query || (field.value ?? '').toLowerCase().includes(query))
      .forEach(field => {
        const [, module, label] = (field.value ?? '').match(MODULE_PREFIX) ?? [null, '', field.value];
        const group = groups.get(module) ?? { label: module, fields: [] };
        group.fields.push({ field, label });
        groups.set(module, group);
      });
    return [...groups.values()];
  });

  readonly visibleFunctions = computed(() =>
    this.isDateResult() || !this.numericFields().length
      ? []
      : AGGREGATE_FUNCTIONS.filter(entry => this.matchesSearch(entry.label, entry.value)),
  );

  readonly showDateFunction = computed(
    () => !!this.dateFields().length && this.matchesSearch(this.dateFunction(), this.dateFunction()),
  );

  chipText(token: FormulaToken): string {
    return tokenText(token, part => this.translate.instant(`modules.fieldConfig.formula.dateParts.${part}`));
  }

  sameField(first: ExpressionField, second: ExpressionField): boolean {
    return first?.key === second?.key;
  }

  toNumber(raw: string): number {
    return raw === '' ? undefined : Number(raw);
  }

  // MARK: keyboard

  onKeydown(event: KeyboardEvent): void {
    if (this.locked() || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const { key } = event;
    if (DIGIT.test(key)) {
      event.preventDefault();
      const secondDot = key === '.' && this.pending().includes('.');
      if (!secondDot && (this.pending() || this.allowed().value)) {
        this.selected.set(null);
        this.pending.update(text => text + key);
      }
      return;
    }
    if (this.symbols.some(symbol => symbol.value === key)) {
      event.preventDefault();
      this.insertSymbol(key);
      return;
    }
    switch (key) {
      case 'Backspace':
        event.preventDefault();
        this.backspace();
        return;
      case 'Delete':
        event.preventDefault();
        this.deleteForward();
        return;
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Home':
      case 'End':
        event.preventDefault();
        this.commitPending();
        this.moveCaret(this.caretAfter(key));
        return;
      case 'Enter':
        event.preventDefault();
        this.commitPending();
        return;
      case 'Escape':
        if (this.pending() || this.selected() !== null) {
          event.stopPropagation();
          this.pending.set('');
          this.selected.set(null);
        }
        return;
    }
    if (LETTER.test(key)) {
      event.preventDefault();
      this.search.set(key);
      this.searchInput()?.nativeElement.focus();
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.insertFirstMatch();
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      this.search.set('');
      this.focusArea();
    }
  }

  onBlur(): void {
    this.focused.set(false);
    this.commitPending();
  }

  // MARK: caret and selection

  moveCaret(position: number): void {
    this.caretIndex.set(Math.max(0, Math.min(position, this.tokens().length)));
    this.selected.set(null);
  }

  /** A click beside the tokens: the typed digits go in first, then the caret goes after everything. */
  moveCaretToEnd(): void {
    this.commitPending();
    this.moveCaret(this.tokens().length);
  }

  select(index: number): void {
    // The typed digits go in at the caret first, which moves a clicked token after them one place on.
    const at = this.caret();
    const target = this.commitPending() && at <= index ? index + 1 : index;
    this.selected.set(target);
    this.caretIndex.set(target + 1);
    this.focusArea();
  }

  backspace(): void {
    if (this.pending()) {
      this.pending.update(text => text.slice(0, -1));
      return;
    }
    if (this.selected() !== null) {
      this.deleteToken(this.selected());
      return;
    }
    const caret = this.caret();
    if (caret > 0) {
      this.deleteToken(caret - 1);
    }
  }

  deleteToken(index: number): void {
    this.deleteAt(index);
    this.selected.set(null);
    this.caretIndex.set(index);
    this.focusArea();
  }

  // MARK: inserting

  insertSymbol(value: string): void {
    if (!this.insertableSymbols().has(value)) {
      return;
    }
    this.insert(
      value === '(' || value === ')'
        ? { type: 'parenthesis', value }
        : { type: 'operator', value: value as FormulaOperator },
    );
  }

  /** On a selected function it sets the column; otherwise it inserts the TOTAL of the field. */
  insertField(field: ExpressionField): void {
    if (this.selectedFunction()) {
      this.setField(this.selected(), 'field', field);
      this.search.set('');
      this.focusArea();
      return;
    }
    if (this.allowed().value) {
      this.insert({ type: 'function', functionKey: AGGREGATE_FUNCTIONS[0].key, field });
      this.search.set('');
    }
  }

  /** On a selected function it changes the function; otherwise it inserts one waiting for its column. */
  chooseFunction(functionKey: number): void {
    if (this.selectedFunction()) {
      this.setFunction(this.selected(), functionKey);
      this.focusArea();
      return;
    }
    if (this.allowed().value) {
      this.insert({ type: 'function', functionKey }, true);
      this.search.set('');
    }
  }

  insertDateFunction(): void {
    if (this.allowed().date) {
      this.insert(newDateToken(this.isDateResult()), true);
      this.search.set('');
    }
  }

  useSecondField(index: number, useField: boolean): void {
    this.patch(index, { useSecondField: useField, secondField: undefined, secondNumber: undefined });
  }

  // MARK: private

  private insert(token: FormulaToken, select = false): void {
    this.commitPending();
    const at = this.caret();
    this.insertAt(at, token);
    this.caretIndex.set(at + 1);
    this.selected.set(select ? at : null);
    this.focusArea();
  }

  /** Puts the typed digits in at the caret as a number, telling whether there was one. */
  private commitPending(): boolean {
    const value = this.pendingValue();
    this.pending.set('');
    if (value === null) {
      return false;
    }
    const at = this.caret();
    this.insertAt(at, { type: 'number', value });
    this.caretIndex.set(at + 1);
    return true;
  }

  private deleteForward(): void {
    if (this.pending()) {
      return;
    }
    if (this.selected() !== null) {
      this.deleteToken(this.selected());
    } else if (this.caret() < this.tokens().length) {
      this.deleteToken(this.caret());
    }
  }

  private caretAfter(key: string): number {
    switch (key) {
      case 'ArrowLeft':
        return this.caret() - 1;
      case 'ArrowRight':
        return this.caret() + 1;
      case 'Home':
        return 0;
      default:
        return this.tokens().length;
    }
  }

  private insertFirstMatch(): void {
    const field = this.fieldGroups()[0]?.fields[0]?.field;
    const functionKey = this.visibleFunctions()[0]?.key;
    if (field && this.acceptsValue()) {
      this.insertField(field);
    } else if (functionKey && this.acceptsValue()) {
      this.chooseFunction(functionKey);
    } else if (this.showDateFunction()) {
      this.insertDateFunction();
    }
  }

  private matchesSearch(...texts: string[]): boolean {
    const query = this.search().trim().toLowerCase();
    return !query || texts.some(text => text.toLowerCase().includes(query));
  }

  private tokenOf<T extends FormulaToken>(type: FormulaToken['type']): T {
    const token = this.selectedToken();
    return token?.type === type ? (token as T) : null;
  }

  private focusArea(): void {
    this.area()?.nativeElement.focus();
  }
}
